"use client";

import { Fragment } from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { ArrowRightIcon } from "lucide-react";
import { TabletFrame } from "@/app/features/[slug]/feature-showcase-section";
import { FEATURE_GROUPS } from "./features-overview-data";
import { SectionTabs } from "./section-tabs";

const fadeUp = (delay = 0) => ({
  initial: { y: 30, opacity: 0 },
  whileInView: { y: 0, opacity: 1 },
  viewport: { once: true },
  transition: { duration: 0.5, delay, ease: [0.21, 0.47, 0.32, 0.98] },
});

function Hero() {
  return (
    <section className="relative bg-[#efecea]">
      <div className="mx-auto max-w-7xl px-4 pb-16 pt-8 text-center md:pb-20 md:pt-12">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-fe-primary">Zobliss features</p>
        <h1 className="mx-auto mt-3 max-w-3xl text-balance font-display text-3xl font-medium leading-[1.12] tracking-[-0.01em] text-neutral-900 md:text-6xl">
          Why salons, spas, & studios{" "}
          <span className="relative inline-block">
            run on Zobliss
            <span className="absolute -bottom-1 left-[10%] h-1 w-4/5 rounded-full bg-gradient-to-r from-[#f3d673] to-[#ffc53d]" />
          </span>
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-pretty text-base text-neutral-700 sm:text-lg">
          One easy platform for booking, payments, clients, team, and marketing, with a support team that
          answers in minutes, not days.
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            href="/signup"
            className="flex min-h-11 items-center justify-center rounded-lg bg-fe-primary px-5 text-sm font-medium text-white transition hover:ring-4 hover:ring-fe-primary/50"
          >
            Try it for free
          </Link>
          <Link
            href="/get-demo"
            className="flex min-h-11 items-center justify-center rounded-lg bg-white px-5 text-sm font-medium text-neutral-900 transition hover:ring-4 hover:ring-black/5"
          >
            Get a demo
          </Link>
        </div>

        {/* Animates on load (not on scroll) since it's above the fold */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="mx-auto mt-12 max-w-5xl"
        >
          <TabletFrame>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/features/pos-kiosk/kiosk-calendar.webp"
              alt="Zobliss calendar in kiosk mode"
              className="block h-auto w-full"
            />
          </TabletFrame>
        </motion.div>
      </div>
    </section>
  );
}

// Plain card (no background) with a dark icon tile and an underlined link.
function FeatureCard({ item, index }) {
  const Icon = item.icon;

  return (
    <motion.div {...fadeUp((index % 3) * 0.1)} className="h-full">
      <Link href={item.href} className="group flex h-full flex-col">
        <span className="w-fit rounded-3xl bg-neutral-800 p-4">
          <Icon className="size-7 text-white md:size-8" strokeWidth={1.5} />
        </span>
        <h3 className="mt-8 text-xl text-neutral-900 md:text-2xl">{item.title}</h3>
        <p className="mt-4 flex-1 text-base text-neutral-600">{item.description}</p>
        {/* Compact link; on hover the gap widens and the underline stretches with it */}
        <span className="mt-7 inline-flex items-center gap-3 self-start border-b border-neutral-900 pb-1.5 font-medium text-neutral-900 transition-[gap] duration-300 group-hover:gap-12">
          {item.cta}
          <ArrowRightIcon className="size-4" />
        </span>
      </Link>
    </motion.div>
  );
}

function FeatureGroup({ group }) {
  const words = `${group.lead} ${group.accent}`.split(" ");
  const lastWord = words.pop();
  const headingStart = words.join(" ");

  return (
    <section id={group.id} className="scroll-mt-24 bg-white py-16 md:py-28">
      <div className="mx-auto max-w-4xl px-4 text-center">
        {/* Home page SectionHeading underline ("Testimonials"), under the last word only */}
        <h2 className="font-display text-3xl font-medium leading-[1.14] tracking-[-0.03em] text-neutral-900 md:text-[4rem]">
          {headingStart}{" "}
          <span className="relative inline-block">
            {lastWord}
            <span className="absolute -bottom-2 start-0 h-1 w-full rounded-full bg-linear-to-r from-[#ff9966] to-[#ffc53d]" />
          </span>
        </h2>
        <p className="mt-8 text-pretty text-base text-neutral-600 sm:text-lg">{group.intro}</p>
      </div>

      <div className="mx-auto mt-14 grid max-w-7xl gap-x-10 gap-y-14 px-4 sm:grid-cols-2 md:mt-20 lg:grid-cols-3">
        {group.items.map((item, itemIndex) => (
          <FeatureCard key={item.href} item={item} index={itemIndex} />
        ))}
      </div>
    </section>
  );
}

export function FeaturesOverview() {
  return (
    <>
      <Hero />
      {FEATURE_GROUPS.map((group, index) => (
        <Fragment key={group.id}>
          {/* Thin line between sections, inside the content width */}
          {index > 0 && (
            <div className="bg-white px-4">
              <hr className="mx-auto max-w-7xl border-neutral-300" />
            </div>
          )}
          <FeatureGroup group={group} />
        </Fragment>
      ))}
      <SectionTabs groups={FEATURE_GROUPS} />
    </>
  );
}
